import { token } from '@atlaskit/tokens';
import {
  type ActionMeta,
  AsyncSelect,
  type StylesConfig,
} from '@databases/atlaskit/Select';
import { messages } from '@databases/intl/messages';
import { autoCompleteLabel } from '@databases/utils/confluence/contentLabel';
import debounce from 'debounce-promise';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

export interface PageLabelsPickerOption {
  label: string;
  value: string;
}

const styles: StylesConfig<PageLabelsPickerOption, boolean> = {
  multiValue: (base) => ({
    ...base,
    border: `1px solid ${token('color.border', '#091E4224')}`,
    borderRadius: token('radius.small', '3px'),
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: token('color.text', '#172B4D'),
  }),
  multiValueRemove: (base) => ({
    ...base,
    backgroundColor: 'transparent',
    borderRadius: token('radius.small', '3px'),
  }),
};

function getOptionLabel(option: PageLabelsPickerOption): string {
  return option.label;
}

function getOptionValue(option: PageLabelsPickerOption): string {
  return option.value;
}

export interface PageLabelsPickerProps {
  value: string[];
  onChange: (value: string[], action: ActionMeta<PageLabelsPickerOption>) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isSeamless?: boolean;
  isInvalid?: boolean;
  isLoading?: boolean;
  onInputChange?: () => void;
  linkedPageContentId: string | undefined;
}

export const getMappedPageLabels = (value: string[]) =>
  value.map((v) => ({ label: v, value: v }));

/**
 * Renders a multi select for page labels with autocomplete.
 */
const PageLabelsPicker: FunctionComponent<PageLabelsPickerProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isInvalid,
  isSeamless,
  isLoading,
  onInputChange,
  linkedPageContentId,
}) => {
  const { formatMessage } = useIntl();

  const [inputValue, setInputValue] = useState('');

  const handleChange = useCallback(
    (
      value: ReadonlyArray<PageLabelsPickerOption>,
      action: ActionMeta<PageLabelsPickerOption>
    ) => {
      onChange(
        value.map((o) => o.value),
        action
      );
    },
    [onChange]
  );

  const loadOptions = useCallback(async (): Promise<PageLabelsPickerOption[]> => {
    const query = inputValue.trim();
    const lowerQuery = query.toLowerCase();
    const labels = await autoCompleteLabel(query, linkedPageContentId);
    if (!query || labels.some((label) => label.toLowerCase() == lowerQuery)) {
      return labels.map((v) => ({ label: v, value: v }));
    } else {
      return [
        {
          label: formatMessage(
            messages['field-settings.type-page-label-create-label-text'],
            { query }
          ),
          value: query,
        },
        ...labels.map((v) => ({ label: v, value: v })),
      ];
    }
  }, [formatMessage, inputValue, linkedPageContentId]);

  const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 400), [loadOptions]);

  const mappedValue: PageLabelsPickerOption[] = useMemo(
    () => getMappedPageLabels(value),
    [value]
  );

  const handleSetInputValue = useCallback(
    (newValue: string) => {
      setInputValue(newValue.replaceAll(' ', '-'));
      onInputChange?.();
    },
    [onInputChange]
  );

  return (
    <AsyncSelect<PageLabelsPickerOption, true>
      inputValue={inputValue}
      onInputChange={handleSetInputValue}
      loadOptions={debouncedLoadOptions}
      value={mappedValue}
      onChange={handleChange}
      isClearable
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      isMulti
      isSeamless={isSeamless}
      isInvalid={isInvalid}
      isLoading={isLoading}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      defaultMenuIsOpen={autoFocus && !mappedValue.length}
      styles={styles}
    />
  );
};

export default PageLabelsPicker;
